/**
 * Assignee picker shown before a board changes pipeline stage: a stage change
 * REQUIRES at least one assignee — the transition materializes a task for
 * whoever owns the new stage on the shared rainvic-tool task board.
 * Used by the dashboard's status board (drag between columns) and the board
 * screen's stage dropdown.
 *
 * The roster list itself lives in `ui/RosterPicker` — the F-126 BGM review
 * asks the same question, and one component keeps both asking it identically.
 */
import {
  BOARD_STATUS_LABELS,
  shortsCrewRoleFor,
  type BoardStatus,
  type BoardSummary,
  type ChangeBoardStatusResponse,
  type TeamMember,
} from "@kola/shared";
import { useEffect, useRef, useState } from "react";

import { changeBoardStatus, fetchShortsCrew } from "../../core/api.js";
import { Modal } from "../../ui/Modal.js";
import { assigneesFrom, RosterPicker } from "../../ui/RosterPicker.js";

export function AssignStatusDialog({
  board,
  toStatus,
  onClose,
  onDone,
}: {
  board: BoardSummary;
  toStatus: BoardStatus;
  onClose: () => void;
  /** Called after a successful transition, with the server's response. */
  onDone: (response: ChangeBoardStatusResponse) => void;
}) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** F-142: the crew role the picker was prefilled from, once it has been. */
  const [prefilled, setPrefilled] = useState<string | null>(null);
  const touched = useRef(false);

  // F-142: on a Shorts week the picker opens already filled from the standing
  // crew for this stage — unless the user has touched it first, in which case
  // their choice stands. A crew that cannot be read just means a blank picker.
  useEffect(() => {
    if (board.kind !== "shorts") return;
    let cancelled = false;
    const role = shortsCrewRoleFor(toStatus);
    fetchShortsCrew()
      .then((crew) => {
        if (cancelled || touched.current) return;
        const uids = crew[role].map((a) => a.uid);
        if (uids.length === 0) return;
        setSelected(new Set(uids));
        setPrefilled(role);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [board.kind, toStatus]);

  const toggle = (uid: string) => {
    touched.current = true;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await changeBoardStatus(
        board.id,
        toStatus,
        assigneesFrom(team, selected),
      );
      onDone(response);
    } catch (err) {
      setSaving(false);
      setError(err instanceof Error ? err.message : "Status change failed");
    }
  };

  return (
    <Modal
      title={`Move to ${BOARD_STATUS_LABELS[toStatus]}`}
      onClose={saving ? () => undefined : onClose}
    >
      <div className="assign-dialog">
        <p className="assign-dialog-lead">
          Who is in charge of <strong>{BOARD_STATUS_LABELS[toStatus]}</strong> for{" "}
          <strong>{board.name}</strong>? A task is created for them.
        </p>

        <RosterPicker
          selected={selected}
          onToggle={toggle}
          onLoaded={setTeam}
          disabled={saving}
        />

        {prefilled !== null && (
          <p className="assign-dialog-lead create-board-hint">
            Prefilled with the Shorts crew&apos;s {prefilled} — change it for a week that differs.
          </p>
        )}

        {error !== null && <div className="error-note">{error}</div>}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={selected.size === 0 || saving}
          >
            {saving ? "Moving…" : `Move & assign${selected.size > 1 ? ` (${selected.size})` : ""}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
